import { useState } from "react";
import { useQueryClient, QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";

type InvalidateTarget = { queryKey: QueryKey; exact?: boolean } | QueryKey;
type OptimisticTarget = { queryKey: QueryKey; exact?: boolean } | QueryKey;

interface UseOptimisticUpdateOptions<TData> {
  makeOptimistic: (previous: TData) => TData;
  mutation: () => Promise<unknown>;
  queryKey: OptimisticTarget;
  invalidate?: InvalidateTarget | InvalidateTarget[];
  onErrorMessage?: string;
}

export function useOptimisticUpdate<TData>() {
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);

  async function run(options: UseOptimisticUpdateOptions<TData>) {
    const { makeOptimistic, mutation, queryKey, invalidate, onErrorMessage } = options;


    // normalize the query key to an object with queryKey and exact properties
    const optimisticTarget = (() => {
      if (Array.isArray(queryKey)) return { queryKey, exact: true };
      if (
        queryKey &&
        typeof queryKey === "object" &&
        "queryKey" in (queryKey as { queryKey?: QueryKey })
      ) {
        return queryKey as { queryKey: QueryKey; exact?: boolean };
      }
      return { queryKey: queryKey as QueryKey, exact: true };
    })();

    const previousEntries = queryClient.getQueriesData<TData>({
      queryKey: optimisticTarget.queryKey,
      exact: optimisticTarget.exact ?? true,
    });

    for (const [key, previous] of previousEntries) {
      if (previous !== undefined) {
        const next = makeOptimistic(previous);
        queryClient.setQueryData<TData>(key, next);
      }
    }

    setIsPending(true);
    try {
      await mutation();
      const targets = Array.isArray(invalidate) ? invalidate : invalidate ? [invalidate] : [];
      for (const target of targets) {
        if (Array.isArray(target)) {
          queryClient.invalidateQueries({ queryKey: target });
          // eslint-disable-next-line
        } else if (target && "queryKey" in (target as any)) {
          const t = target as { queryKey: QueryKey; exact?: boolean };
          queryClient.invalidateQueries({ queryKey: t.queryKey, exact: t.exact });
        } else if (target) {
          queryClient.invalidateQueries({ queryKey: target as QueryKey });
        }
      }
    } catch {
      for (const [key, previous] of previousEntries) {
        queryClient.setQueryData(key, previous);
      }
      if (onErrorMessage) {
        toast.error(onErrorMessage);
      }
    } finally {
      setIsPending(false);
    }
  }

  return { run, isPending };
}

export default useOptimisticUpdate;


